<template>
  <div>
    <b-card-title class="mb-2">
      {{ $t('settings.coordinates.tools.title') }}
    </b-card-title>
    <b-card-text class="mb-3 text-muted">
      {{ $t('settings.coordinates.tools.description') }}
    </b-card-text>

    <b-tabs pills>
      <!-- Comprehensive Test Tab -->
      <b-tab
        :title="$t('settings.coordinates.tools.comprehensive_test')"
        active
      >
        <b-card>
          <b-card-title>{{ $t('settings.coordinates.tools.test_all_conversions') }}</b-card-title>
          <b-card-text class="text-muted">
            {{ $t('settings.coordinates.tools.test_all_conversions_desc') }}
          </b-card-text>

          <b-form @submit.prevent="handleTestConversions">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.latitude')"
                  label-for="test-latitude"
                >
                  <b-form-input
                    id="test-latitude"
                    v-model.number="testForm.latitude"
                    type="number"
                    step="0.000001"
                    min="-90"
                    max="90"
                    :placeholder="$t('settings.coordinates.tools.latitude_placeholder')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.longitude')"
                  label-for="test-longitude"
                >
                  <b-form-input
                    id="test-longitude"
                    v-model.number="testForm.longitude"
                    type="number"
                    step="0.000001"
                    min="-180"
                    max="180"
                    :placeholder="$t('settings.coordinates.tools.longitude_placeholder')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="loading"
                >
                  <b-spinner
                    v-if="loading"
                    small
                    class="mr-50"
                  />
                  <feather-icon
                    icon="RefreshCwIcon"
                    class="mr-50"
                  />
                  {{ $t('settings.coordinates.tools.test_all') }}
                </b-button>
              </b-col>

              <!-- Comprehensive Test Results -->
              <b-col
                v-if="testResult"
                cols="12"
                class="mt-3"
              >
                <!-- Input Section -->
                <b-card
                  bg-variant="light"
                  class="mb-2"
                >
                  <h5 class="mb-2">
                    <feather-icon
                      icon="MapPinIcon"
                      class="mr-50"
                    />
                    {{ $t('settings.coordinates.tools.input_coordinates') }}
                  </h5>
                  <p class="mb-0">
                    <strong>{{ $t('settings.coordinates.tools.latitude') }}:</strong> {{ testResult.input.latitude }}° |
                    <strong>{{ $t('settings.coordinates.tools.longitude') }}:</strong> {{ testResult.input.longitude }}°
                  </p>
                </b-card>

                <!-- Validation Section -->
                <b-card class="mb-2">
                  <h5 class="mb-2">
                    <feather-icon
                      icon="CheckCircleIcon"
                      class="mr-50"
                    />
                    {{ $t('settings.coordinates.tools.validation') }}
                  </h5>
                  <b-row>
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <b-alert
                        :variant="testResult.validation.is_within_egypt_bounds ? 'success' : 'warning'"
                        show
                      >
                        <feather-icon
                          :icon="testResult.validation.is_within_egypt_bounds ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                          class="mr-50"
                        />
                        {{ testResult.validation.is_within_egypt_bounds ? $t('settings.coordinates.tools.within_bounds') : $t('settings.coordinates.tools.outside_bounds') }}
                      </b-alert>
                    </b-col>
                    <b-col
                      cols="12"
                      md="6"
                    >
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.tools.valid_latitude') }}:</strong>
                        <b-badge :variant="testResult.validation.is_valid_latitude ? 'success' : 'danger'">
                          {{ testResult.validation.is_valid_latitude ? $t('common.yes') : $t('common.no') }}
                        </b-badge>
                      </p>
                      <p class="mb-0">
                        <strong>{{ $t('settings.coordinates.tools.valid_longitude') }}:</strong>
                        <b-badge :variant="testResult.validation.is_valid_longitude ? 'success' : 'danger'">
                          {{ testResult.validation.is_valid_longitude ? $t('common.yes') : $t('common.no') }}
                        </b-badge>
                      </p>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- Conversions Section -->
                <b-card class="mb-2">
                  <h5 class="mb-2">
                    <feather-icon
                      icon="RefreshCwIcon"
                      class="mr-50"
                    />
                    {{ $t('settings.coordinates.tools.conversions') }}
                  </h5>

                  <!-- DMS Format -->
                  <div class="mb-3">
                    <h6 class="text-primary">
                      {{ $t('settings.coordinates.tools.dms_format') }}
                    </h6>
                    <p class="mb-1">
                      <strong>{{ $t('settings.coordinates.tools.latitude') }}:</strong>
                      {{ testResult.conversions.dms.formatted.latitude }}
                    </p>
                    <p class="mb-1">
                      <strong>{{ $t('settings.coordinates.tools.longitude') }}:</strong>
                      {{ testResult.conversions.dms.formatted.longitude }}
                    </p>
                    <p class="mb-0 text-muted">
                      <small>{{ testResult.conversions.dms.formatted.combined }}</small>
                    </p>
                  </div>

                  <!-- Radians -->
                  <div class="mb-3">
                    <h6 class="text-primary">
                      {{ $t('settings.coordinates.tools.radians_format') }}
                    </h6>
                    <p class="mb-1">
                      <strong>{{ $t('settings.coordinates.tools.latitude') }}:</strong>
                      {{ testResult.conversions.radians.latitude.toFixed(10) }}
                    </p>
                    <p class="mb-0">
                      <strong>{{ $t('settings.coordinates.tools.longitude') }}:</strong>
                      {{ testResult.conversions.radians.longitude.toFixed(10) }}
                    </p>
                  </div>

                  <!-- Decimal Degrees -->
                  <div>
                    <h6 class="text-primary">
                      {{ $t('settings.coordinates.tools.decimal_format') }}
                    </h6>
                    <p class="mb-1">
                      <strong>{{ $t('settings.coordinates.tools.latitude') }}:</strong>
                      {{ testResult.conversions.decimal_degrees.latitude }}°
                    </p>
                    <p class="mb-0">
                      <strong>{{ $t('settings.coordinates.tools.longitude') }}:</strong>
                      {{ testResult.conversions.decimal_degrees.longitude }}°
                    </p>
                  </div>
                </b-card>

                <!-- Coordinate Systems Section -->
                <b-card class="mb-2">
                  <h5 class="mb-2">
                    <feather-icon
                      icon="GlobeIcon"
                      class="mr-50"
                    />
                    {{ $t('settings.coordinates.tools.coordinate_systems') }}
                  </h5>
                  <b-row>
                    <b-col
                      cols="12"
                      md="4"
                    >
                      <h6 class="text-primary">
                        UTM
                      </h6>
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.utm.zone') }}:</strong> {{ testResult.coordinate_systems.utm.zone }}
                      </p>
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.utm.hemisphere') }}:</strong> {{ testResult.coordinate_systems.utm.hemisphere }}
                      </p>
                      <p class="mb-0 text-muted">
                        <small>{{ testResult.coordinate_systems.utm.description }}</small>
                      </p>
                    </b-col>
                    <b-col
                      cols="12"
                      md="4"
                    >
                      <h6 class="text-primary">
                        ETM
                      </h6>
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.etm.central_meridian') }}:</strong> {{ testResult.coordinate_systems.etm.central_meridian }}°
                      </p>
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.etm.latitude_of_origin') }}:</strong> {{ testResult.coordinate_systems.etm.latitude_of_origin }}°
                      </p>
                      <p class="mb-0 text-muted">
                        <small>{{ testResult.coordinate_systems.etm.description }}</small>
                      </p>
                    </b-col>
                    <b-col
                      cols="12"
                      md="4"
                    >
                      <h6 class="text-primary">
                        WGS84
                      </h6>
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.tools.datum') }}:</strong> {{ testResult.coordinate_systems.wgs84.datum }}
                      </p>
                      <p class="mb-1">
                        <strong>{{ $t('settings.coordinates.tools.ellipsoid') }}:</strong> {{ testResult.coordinate_systems.wgs84.ellipsoid }}
                      </p>
                      <p class="mb-0 text-muted">
                        <small>{{ $t('settings.coordinates.wgs84.semi_major_axis') }}: {{ testResult.coordinate_systems.wgs84.semi_major_axis_meters }}m</small>
                      </p>
                    </b-col>
                  </b-row>
                </b-card>

                <!-- Map Links Section -->
                <b-card bg-variant="info">
                  <h5 class="mb-2 text-white">
                    <feather-icon
                      icon="MapIcon"
                      class="mr-50"
                    />
                    {{ $t('settings.coordinates.tools.view_on_map') }}
                  </h5>
                  <b-button
                    variant="light"
                    size="sm"
                    :href="testResult.examples.google_maps_url"
                    target="_blank"
                    class="mr-1"
                  >
                    <feather-icon
                      icon="ExternalLinkIcon"
                      class="mr-50"
                    />
                    Google Maps
                  </b-button>
                  <b-button
                    variant="light"
                    size="sm"
                    :href="testResult.examples.openstreetmap_url"
                    target="_blank"
                  >
                    <feather-icon
                      icon="ExternalLinkIcon"
                      class="mr-50"
                    />
                    OpenStreetMap
                  </b-button>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </b-card>
      </b-tab>

      <!-- DMS Conversion Tab -->
      <b-tab :title="$t('settings.coordinates.tools.dms_conversion')">
        <b-card>
          <b-card-title>{{ $t('settings.coordinates.tools.decimal_to_dms') }}</b-card-title>
          <b-form @submit.prevent="handleConvertToDMS">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.latitude')"
                  label-for="dms-latitude"
                >
                  <b-form-input
                    id="dms-latitude"
                    v-model.number="dmsForm.latitude"
                    type="number"
                    step="0.000001"
                    min="-90"
                    max="90"
                    :placeholder="$t('settings.coordinates.tools.latitude_placeholder')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.longitude')"
                  label-for="dms-longitude"
                >
                  <b-form-input
                    id="dms-longitude"
                    v-model.number="dmsForm.longitude"
                    type="number"
                    step="0.000001"
                    min="-180"
                    max="180"
                    :placeholder="$t('settings.coordinates.tools.longitude_placeholder')"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="loading"
                >
                  <b-spinner
                    v-if="loading"
                    small
                    class="mr-50"
                  />
                  {{ $t('settings.coordinates.tools.convert') }}
                </b-button>
              </b-col>

              <!-- DMS Result -->
              <b-col
                v-if="dmsResult"
                cols="12"
                class="mt-3"
              >
                <b-card bg-variant="light">
                  <h5>{{ $t('settings.coordinates.tools.result') }}</h5>
                  <p class="mb-1">
                    <strong>{{ $t('settings.coordinates.tools.latitude') }}:</strong>
                    {{ dmsResult.formatted.latitude }}
                  </p>
                  <p class="mb-1">
                    <strong>{{ $t('settings.coordinates.tools.longitude') }}:</strong>
                    {{ dmsResult.formatted.longitude }}
                  </p>
                  <hr>
                  <p class="mb-0">
                    <small class="text-muted">
                      {{ dmsResult.latitude.degrees }}° {{ dmsResult.latitude.minutes }}' {{ dmsResult.latitude.seconds }}" {{ dmsResult.latitude.direction }},
                      {{ dmsResult.longitude.degrees }}° {{ dmsResult.longitude.minutes }}' {{ dmsResult.longitude.seconds }}" {{ dmsResult.longitude.direction }}
                    </small>
                  </p>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </b-card>

        <!-- Decimal Conversion Card -->
        <b-card class="mt-2">
          <b-card-title>{{ $t('settings.coordinates.tools.dms_to_decimal') }}</b-card-title>
          <b-form @submit.prevent="handleConvertToDecimal">
            <h6 class="mb-2">
              {{ $t('settings.coordinates.tools.latitude') }}
            </h6>
            <b-row>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.degrees')">
                  <b-form-input
                    v-model.number="decimalForm.latitude.degrees"
                    type="number"
                    min="0"
                    max="90"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.minutes')">
                  <b-form-input
                    v-model.number="decimalForm.latitude.minutes"
                    type="number"
                    min="0"
                    max="59"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.seconds')">
                  <b-form-input
                    v-model.number="decimalForm.latitude.seconds"
                    type="number"
                    min="0"
                    max="60"
                    step="0.01"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.direction')">
                  <b-form-select
                    v-model="decimalForm.latitude.direction"
                    :options="latDirectionOptions"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <h6 class="mb-2 mt-2">
              {{ $t('settings.coordinates.tools.longitude') }}
            </h6>
            <b-row>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.degrees')">
                  <b-form-input
                    v-model.number="decimalForm.longitude.degrees"
                    type="number"
                    min="0"
                    max="180"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.minutes')">
                  <b-form-input
                    v-model.number="decimalForm.longitude.minutes"
                    type="number"
                    min="0"
                    max="59"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.seconds')">
                  <b-form-input
                    v-model.number="decimalForm.longitude.seconds"
                    type="number"
                    min="0"
                    max="60"
                    step="0.01"
                    required
                  />
                </b-form-group>
              </b-col>
              <b-col
                cols="12"
                md="3"
              >
                <b-form-group :label="$t('settings.coordinates.tools.direction')">
                  <b-form-select
                    v-model="decimalForm.longitude.direction"
                    :options="lngDirectionOptions"
                    required
                  />
                </b-form-group>
              </b-col>
            </b-row>

            <b-button
              variant="primary"
              type="submit"
              :disabled="loading"
            >
              <b-spinner
                v-if="loading"
                small
                class="mr-50"
              />
              {{ $t('settings.coordinates.tools.convert') }}
            </b-button>

            <!-- Decimal Result -->
            <b-card
              v-if="decimalResult"
              bg-variant="light"
              class="mt-3"
            >
              <h5>{{ $t('settings.coordinates.tools.result') }}</h5>
              <p class="mb-1">
                <strong>{{ $t('settings.coordinates.tools.latitude') }}:</strong> {{ decimalResult.latitude }}°
              </p>
              <p class="mb-0">
                <strong>{{ $t('settings.coordinates.tools.longitude') }}:</strong> {{ decimalResult.longitude }}°
              </p>
            </b-card>
          </b-form>
        </b-card>
      </b-tab>

      <!-- Bounds Validation Tab -->
      <b-tab :title="$t('settings.coordinates.tools.bounds_validation')">
        <b-card>
          <b-card-title>{{ $t('settings.coordinates.tools.validate_egypt_bounds') }}</b-card-title>
          <b-card-text class="text-muted">
            {{ $t('settings.coordinates.tools.validate_egypt_bounds_desc') }}
          </b-card-text>

          <b-form @submit.prevent="handleValidateBounds">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.latitude')"
                  label-for="validate-latitude"
                >
                  <b-form-input
                    id="validate-latitude"
                    v-model.number="boundsForm.latitude"
                    type="number"
                    step="0.000001"
                    min="-90"
                    max="90"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="6"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.longitude')"
                  label-for="validate-longitude"
                >
                  <b-form-input
                    id="validate-longitude"
                    v-model.number="boundsForm.longitude"
                    type="number"
                    step="0.000001"
                    min="-180"
                    max="180"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="loading"
                >
                  <b-spinner
                    v-if="loading"
                    small
                    class="mr-50"
                  />
                  {{ $t('settings.coordinates.tools.validate') }}
                </b-button>
              </b-col>

              <!-- Validation Result -->
              <b-col
                v-if="boundsResult !== null"
                cols="12"
                class="mt-3"
              >
                <b-alert
                  :variant="boundsResult.is_within_egypt_bounds ? 'success' : 'warning'"
                  show
                >
                  <div class="alert-body">
                    <feather-icon
                      :icon="boundsResult.is_within_egypt_bounds ? 'CheckCircleIcon' : 'AlertCircleIcon'"
                      class="mr-50"
                    />
                    <strong>
                      {{
                        boundsResult.is_within_egypt_bounds
                          ? $t('settings.coordinates.tools.within_bounds')
                          : $t('settings.coordinates.tools.outside_bounds')
                      }}
                    </strong>
                  </div>
                </b-alert>
              </b-col>
            </b-row>
          </b-form>
        </b-card>
      </b-tab>

      <!-- Unit Conversion Tab -->
      <b-tab :title="$t('settings.coordinates.tools.unit_conversion')">
        <b-card>
          <b-card-title>{{ $t('settings.coordinates.tools.convert_units') }}</b-card-title>
          <b-form @submit.prevent="handleConvertUnits">
            <b-row>
              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.value')"
                  label-for="unit-value"
                >
                  <b-form-input
                    id="unit-value"
                    v-model.number="unitsForm.value"
                    type="number"
                    step="0.000001"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.from_unit')"
                  label-for="from-unit"
                >
                  <b-form-select
                    id="from-unit"
                    v-model="unitsForm.from"
                    :options="unitOptions"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col
                cols="12"
                md="4"
              >
                <b-form-group
                  :label="$t('settings.coordinates.tools.to_unit')"
                  label-for="to-unit"
                >
                  <b-form-select
                    id="to-unit"
                    v-model="unitsForm.to"
                    :options="unitOptions"
                    required
                  />
                </b-form-group>
              </b-col>

              <b-col cols="12">
                <b-button
                  variant="primary"
                  type="submit"
                  :disabled="loading"
                >
                  <b-spinner
                    v-if="loading"
                    small
                    class="mr-50"
                  />
                  {{ $t('settings.coordinates.tools.convert') }}
                </b-button>
              </b-col>

              <!-- Unit Conversion Result -->
              <b-col
                v-if="unitsResult"
                cols="12"
                class="mt-3"
              >
                <b-card bg-variant="light">
                  <h5>{{ $t('settings.coordinates.tools.result') }}</h5>
                  <p class="mb-0">
                    <strong>{{ unitsResult.input }} {{ unitsResult.from }}</strong>
                    =
                    <strong>{{ unitsResult.result }} {{ unitsResult.to }}</strong>
                  </p>
                </b-card>
              </b-col>
            </b-row>
          </b-form>
        </b-card>
      </b-tab>
    </b-tabs>
  </div>
</template>

<script>
import {
  BCardTitle,
  BCardText,
  BCard,
  BTabs,
  BTab,
  BForm,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BRow,
  BCol,
  BButton,
  BSpinner,
  BAlert,
  BBadge,
} from 'bootstrap-vue'
import { mapActions, mapState } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  name: 'ConversionTools',
  components: {
    BCardTitle,
    BCardText,
    BCard,
    BTabs,
    BTab,
    BForm,
    BFormGroup,
    BFormInput,
    BFormSelect,
    BRow,
    BCol,
    BButton,
    BSpinner,
    BAlert,
    BBadge,
  },
  computed: {
    ...mapState('coordinates', {
      loading: state => state.loading,
    }),
  },
  data() {
    return {
      testForm: {
        latitude: null,
        longitude: null,
      },
      testResult: null,
      dmsForm: {
        latitude: null,
        longitude: null,
      },
      dmsResult: null,
      decimalForm: {
        latitude: {
          degrees: 0,
          minutes: 0,
          seconds: 0,
          direction: 'N',
        },
        longitude: {
          degrees: 0,
          minutes: 0,
          seconds: 0,
          direction: 'E',
        },
      },
      decimalResult: null,
      boundsForm: {
        latitude: null,
        longitude: null,
      },
      boundsResult: null,
      unitsForm: {
        value: null,
        from: 'meters',
        to: 'feet',
      },
      unitsResult: null,
      latDirectionOptions: [
        { value: 'N', text: this.$t('settings.coordinates.tools.north') },
        { value: 'S', text: this.$t('settings.coordinates.tools.south') },
      ],
      lngDirectionOptions: [
        { value: 'E', text: this.$t('settings.coordinates.tools.east') },
        { value: 'W', text: this.$t('settings.coordinates.tools.west') },
      ],
      unitOptions: [
        { value: 'meters', text: this.$t('settings.coordinates.tools.meters') },
        { value: 'feet', text: this.$t('settings.coordinates.tools.feet') },
        { value: 'degrees', text: this.$t('settings.coordinates.tools.degrees_unit') },
        { value: 'radians', text: this.$t('settings.coordinates.tools.radians') },
      ],
    }
  },
  methods: {
    ...mapActions('coordinates', [
      'testConversions',
      'convertToDMS',
      'convertToDecimal',
      'validateEgyptBounds',
      'convertUnits',
    ]),

    async handleTestConversions() {
      try {
        const response = await this.testConversions({ ...this.testForm })
        this.testResult = response.data
        this.showSuccess(this.$t('settings.coordinates.tools.test_success'))
      } catch (error) {
        this.testResult = null
        this.showError(error.message || this.$t('settings.coordinates.errors.conversion_failed'))
      }
    },

    async handleConvertToDMS() {
      try {
        const response = await this.convertToDMS({ ...this.dmsForm })
        this.dmsResult = response.data
        this.showSuccess(`${this.$t('settings.coordinates.tools.convert')} ${this.$t('common.success')}`)
      } catch (error) {
        this.dmsResult = null
        this.showError(error.message || this.$t('settings.coordinates.errors.conversion_failed'))
      }
    },

    async handleConvertToDecimal() {
      try {
        const response = await this.convertToDecimal({ ...this.decimalForm })
        this.decimalResult = response.data
        this.showSuccess(`${this.$t('settings.coordinates.tools.convert')} ${this.$t('common.success')}`)
      } catch (error) {
        this.decimalResult = null
        this.showError(error.message || this.$t('settings.coordinates.errors.conversion_failed'))
      }
    },

    async handleValidateBounds() {
      try {
        const response = await this.validateEgyptBounds({ ...this.boundsForm })
        this.boundsResult = response.data
      } catch (error) {
        this.boundsResult = null
        this.showError(error.message || this.$t('settings.coordinates.errors.validation_failed'))
      }
    },

    async handleConvertUnits() {
      try {
        const response = await this.convertUnits({ ...this.unitsForm })
        this.unitsResult = response.data
        this.showSuccess(`${this.$t('settings.coordinates.tools.convert')} ${this.$t('common.success')}`)
      } catch (error) {
        this.unitsResult = null
        this.showError(error.message || this.$t('settings.coordinates.errors.conversion_failed'))
      }
    },

    showSuccess(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.success'),
          text: message,
          variant: 'success',
          icon: 'CheckIcon',
        },
      })
    },

    showError(message) {
      this.$toast({
        component: ToastificationContent,
        props: {
          title: this.$t('common.error'),
          text: message,
          variant: 'danger',
          icon: 'XIcon',
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.mr-50 {
  margin-right: 0.5rem;
}
</style>
